import { toast } from "sonner";
import { useForm } from "react-hook-form";
import { storeData } from "@/utils/storage";
import { useTranslation } from "react-i18next";
import { useRegister, useGenerateOtp } from "@/hooks";
import CustomInput from "@/components/custom-input";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { zodResolver } from "@hookform/resolvers/zod";
import { useAuthStore } from "@/store/use-auth-store";
import PrimaryButton from "@/components/button/primary-button";
import { GoogleAuthButton } from "@/components/button/auth-buttons";
import {
  registerSchema,
  type RegisterFormData,
} from "@/validation/auth-schemas";
import { API_URL } from "@/lib/utils";
import { UtilsService } from "@/services/utils/utils.service";
import type { RegisterDto, RegisterResponse } from "@/services/auth";

const utils = new UtilsService();

const RegisterPage = () => {
  const { setUser } = useAuthStore();
  const registerMutation = useRegister();
  const generateOtpMutation = useGenerateOtp(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();
  const invitationToken = searchParams.get("invitationToken");

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<RegisterFormData>({
    resolver: zodResolver(registerSchema),
  });

  const onSubmit = async (data: RegisterFormData) => {
    try {
      const { confirmPassword, ...registerData } = data;

      // Add invitation token if present
      const registerDataWithToken: RegisterDto = {
        ...registerData,
        ...(invitationToken && { invitationToken }),
      };

      // Step 1: Register user
      const response: RegisterResponse = await registerMutation.mutateAsync(registerDataWithToken);

      if (response?.success === true) {
        // Step 2: Send Email Verification OTP
        const otpResponse = await generateOtpMutation.mutateAsync({
          email: data.email,
          type: "email_verification",
        });

        if (otpResponse?.success === true || otpResponse) {
          // Only show success toast after both operations succeed
          toast.success(t("common.otpSentSuccessfully"));

          // Store user data
          storeData("User", {
            firstName: data.firstName,
            lastName: data.lastName,
            email: data.email,
          });

          // Navigate to verification page
          navigate("/auth/verify-email");
        } else {
          // OTP send failed
          const otpError = otpResponse as { error?: string; message?: string } | null;
          const errorMessage =
            otpError?.error ||
            otpError?.message ||
            t("common.otpSendFailed");
          toast.error(errorMessage);
        }
      } else {
        // Registration failed
        toast.error(
          response?.error || response?.message || t("common.operationFailed")
        );
      }
    } catch (error: unknown) {
      // Handle any errors during the registration flow
      const errorMessage = utils.extractErrorMessage(
        error,
        t("auth.registrationFailedPleaseTryAgain")
      );
      toast.error(errorMessage);
    }
  };

  const googleAuth = () => {
    const invitationParam = invitationToken ? `?invitationToken=${invitationToken}` : '';
    window.open(`${API_URL}/auth/google${invitationParam}`, "_self");
  };

  return (
    <main className="flex flex-row items-stretch bg-white text-black h-[100vh]">
      <section className="w-full px-4 overflow-y-auto lg:pb-8 sm:w-6/12 mx-auto sm:min-w-[480px]">
        <img
          src={"/Uzaforms-logo.svg"}
          alt="UzaForms"
          className="block mx-auto mt-16 mb-6"
        />
        <section className="">
          <p className="text-2xl text-black text-center font-medium">
            {t("auth.signUp")}
          </p>
          <p className="text-sm mt-3 lg:mt-4 text-black text-center max-w-[386px] mx-auto">
            {t(
              "auth.joinUzaboxformsAndWatchYourOrganizationGetDigitized"
            )}
          </p>
        </section>
        <section className="auth-providers max-w-[540px] mx-auto flex items-center justify-center space-x-4 mt-8">
          <GoogleAuthButton handlePress={googleAuth} />
        </section>
        <p className="text-sm text-center py-5">Or</p>
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="flex justify-center flex-col items-center space-y-5 max-w-[540px] mx-auto"
        >
          <CustomInput
            placeholder={t("settings.firstName")}
            type="text"
            containerStyles=""
            error={errors.firstName?.message}
            {...register("firstName")}
          />
          <CustomInput
            placeholder={t("settings.lastName")}
            type="text"
            containerStyles=""
            error={errors.lastName?.message}
            {...register("lastName")}
          />
          <CustomInput
            placeholder={t("common.email")}
            type="email"
            containerStyles=""
            error={errors.email?.message}
            {...register("email")}
          />
          <CustomInput
            placeholder={t("common.password")}
            type="password"
            containerStyles="pr-4"
            error={errors.password?.message}
            {...register("password")}
          />
          <CustomInput
            placeholder={t("auth.confirmPassword")}
            type="password"
            containerStyles="pr-4 w-full"
            error={errors.confirmPassword?.message}
            {...register("confirmPassword")}
          />
          <PrimaryButton
            buttonType="submit"
            title={t("auth.signUp")}
            containerStyles="w-full"
            textStyles=""
            loading={
              registerMutation.isPending || generateOtpMutation.isPending
            }
          />
          <div className="flex items-center space-x-1 text-sm">
            <p className="text-black">{t("auth.haveAnAccount")}</p>
            <Link
              to={"/auth/login"}
              className="text-primary text-base font-medium hover:underline"
            >
              {t("auth.signIn")}
            </Link>
          </div>
        </form>
      </section>
      <section className='bg-[url("/Signup-bg.png")] h-[100vh] hidden lg:flex items-end justify-end w-6/12 bg-cover bg-no-repeat pb-8 pr-4'>
        <div className="bg-white rounded-lg w-fit py-10 px-4">
          <p className="text-center text-[#22262D] font-medium">
            {t("auth.poweredBy")}
          </p>
          <h2 className="text-[#001A55] font-bold text-center text-2xl">
            UZABOX
          </h2>
          <p className="text-center text-sm max-w-[420px] mt-6">
            {t(
              "auth.organizeAllYourImportantInformationInOnePlace"
            )}
          </p>
        </div>
      </section>
    </main>
  );
};

export default RegisterPage;
